/**
 * Pure-logic helpers backing the Analytics-detail "Price feed (1s)"
 * section's nested-tab layout.
 *
 * The data-api always emits ticks at 1-second granularity regardless of
 * the market's nominal ``bucket_seconds``. To present that 1s stream
 * usefully across markets ranging from 5-minute up to 4-hour windows,
 * the UI nests tabs as:
 *
 *     window  -> {1-hr ?} -> {15-min ?} -> {5-min ?} -> 1-min leaf
 *
 * "?" tiers are skipped when they are equal to or larger than the
 * window length. The leaf is always 60-second-wide buckets and shows
 * one table row per second.
 *
 *   btc-5m  (300s)   => [1-min leaf]
 *   btc-15m (900s)   => [5-min] -> 1-min leaf
 *   btc-1h  (3600s)  => [15-min] -> [5-min] -> 1-min leaf
 *   btc-4h  (14400s) => [1-hr] -> [15-min] -> [5-min] -> 1-min leaf
 *
 * No state or rendering lives in here so we can unit-test the bucket
 * arithmetic without spinning up Mantine.
 */

export const LEAF_SPAN_SECONDS = 60;

export interface Tier {
  /** Width of this tier's slot in seconds. */
  readonly spanSeconds: number;
  /**
   * Label prefix shown above this tier's tab strip. Combined with the
   * 1-based slot index inside the parent (``#1``, ``#2``, …) or as a
   * specialised "Hour {n}" template at the top of the hour tier.
   */
  readonly labelKind: "hour" | "minutes";
}

/** All possible non-leaf tiers, largest first. */
export const TIER_TABLE: readonly Tier[] = Object.freeze([
  { spanSeconds: 3600, labelKind: "hour" },
  { spanSeconds: 900, labelKind: "minutes" },
  { spanSeconds: 300, labelKind: "minutes" },
]);

/**
 * Pick the tier list (outermost first) that applies to ``windowSeconds``.
 *
 *   300   -> [] (no outer tiers; just leaf)
 *   900   -> [300]
 *   3600  -> [900, 300]
 *   14400 -> [3600, 900, 300]
 *
 * Tiers strictly smaller than ``windowSeconds`` AND strictly larger
 * than the 60s leaf are included. This way the UI never renders a
 * single-tab outer wrapper or a redundant tier that just contains
 * one leaf each.
 */
export function pickTiersForWindow(windowSeconds: number): Tier[] {
  return TIER_TABLE.filter(
    (t) => t.spanSeconds < windowSeconds && t.spanSeconds > LEAF_SPAN_SECONDS,
  );
}

/**
 * Format a tab label for a slot at tier ``tier`` whose 1-based index
 * within its parent is ``indexInParent``.
 */
export function formatTierLabel(tier: Tier, indexInParent: number): string {
  if (tier.labelKind === "hour") {
    return `Hour ${indexInParent}`;
  }
  // 900s -> "15-min #1", 300s -> "5-min #1".
  const minutes = Math.round(tier.spanSeconds / 60);
  return `${minutes}-min #${indexInParent}`;
}

/** Format a leaf (1-min) tab label. ``index`` is 1-based within parent. */
export function formatLeafLabel(indexInParent: number): string {
  return `M${indexInParent}`;
}

export interface SlotNode {
  /** Width of this slot in seconds (one of the tier spans, or 60 for leaves). */
  readonly spanSeconds: number;
  /** 1-based index of this slot within its immediate parent. */
  readonly indexInParent: number;
  /** Inclusive lower bound (seconds since window_start). */
  readonly offsetStart: number;
  /** Exclusive upper bound (seconds since window_start). */
  readonly offsetEnd: number;
  /** Number of populated 1-second ticks inside this subtree. */
  readonly tickCount: number;
  /**
   * Tab label shown above this slot (e.g. ``"Hour 2"``, ``"15-min #3"``,
   * ``"M5"``).
   */
  readonly label: string;
  /**
   * Stable path key for React state ("1-2-3-5" etc.) — uniquely
   * identifies this slot across the entire tree, used as the controlled
   * Tabs `value` key.
   */
  readonly key: string;
  /** Child slots one tier finer than this one. Empty for leaves. */
  readonly children: SlotNode[];
  /**
   * ``true`` when this slot represents the innermost 1-minute leaf.
   * Leaves render the actual price-feed table; non-leaves render
   * another tab strip.
   */
  readonly isLeaf: boolean;
}

/**
 * Build the slot tree for the given window length and a ``tickCount``
 * function that returns the number of populated ticks in
 * ``[offsetStart, offsetEnd)``.
 *
 * The function is the single source of layout truth used by the
 * Analytics-detail Price-feed component: it captures the entire
 * "which-tab-is-where, how-many-ticks-each, how-do-we-key-react-state"
 * computation in one pure function.
 */
export function buildSlotTree(
  windowSeconds: number,
  tickCountInRange: (offsetStart: number, offsetEnd: number) => number,
): SlotNode[] {
  const tiers = pickTiersForWindow(windowSeconds);
  return buildTier(
    /*parentKey*/ "",
    /*parentStart*/ 0,
    /*parentEnd*/ windowSeconds,
    tiers,
    /*depth*/ 0,
    tickCountInRange,
  );
}

function buildTier(
  parentKey: string,
  parentStart: number,
  parentEnd: number,
  tiers: Tier[],
  depth: number,
  tickCountInRange: (start: number, end: number) => number,
): SlotNode[] {
  const currentTier: Tier | undefined = tiers[depth];
  const isLeafTier = currentTier === undefined;
  const span = isLeafTier ? LEAF_SPAN_SECONDS : currentTier.spanSeconds;
  const out: SlotNode[] = [];
  for (let offset = parentStart, idx = 1; offset < parentEnd; offset += span, idx += 1) {
    const slotEnd = Math.min(parentEnd, offset + span);
    const key = parentKey === "" ? String(idx) : `${parentKey}-${idx}`;
    const children = isLeafTier
      ? []
      : buildTier(key, offset, slotEnd, tiers, depth + 1, tickCountInRange);
    const tickCount = isLeafTier
      ? tickCountInRange(offset, slotEnd)
      : children.reduce((sum, c) => sum + c.tickCount, 0);
    const label = isLeafTier
      ? formatLeafLabel(idx)
      : formatTierLabel(currentTier, idx);
    out.push({
      spanSeconds: span,
      indexInParent: idx,
      offsetStart: offset,
      offsetEnd: slotEnd,
      tickCount,
      label,
      key,
      children,
      isLeaf: isLeafTier,
    });
  }
  return out;
}

/**
 * Walk the tree and return the path of slot keys (root → leaf) for the
 * first leaf whose ``tickCount > 0``. Used to default the controlled
 * tab state to "wherever the data actually lives". Returns ``null``
 * when the window is empty.
 */
export function firstPopulatedPath(roots: SlotNode[]): string[] | null {
  for (const r of roots) {
    if (r.tickCount === 0) continue;
    if (r.isLeaf) return [r.key];
    const sub = firstPopulatedPath(r.children);
    if (sub) return [r.key, ...sub];
  }
  return null;
}
